import React from 'react';
import classnames from 'classnames';
import { renderRoutes } from 'react-router-config';
import { RouteComponentProps } from 'react-router';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import { SectionProvider } from '../Sections/SectionContext';
import { ThemeOptions } from '@wix/da-shared-react/pkg/publicSession/types';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import SiteFooter from '@wix/da-shared-react/pkg/SiteFooter';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import ProfileIdentity from '../UserWidgets/ProfileIdentity';
import MoreActionsDropdownMobile from '../UserWidgets/ProfileIdentity/MoreActionsDropdown/Mobile';
import ProfileHeroIntroPopup from '../UserWidgets/ProfileHeroV2/ProfileHeroIntroPopup';
import SubNavBar from '../SubNavBar';
import { BiLoggerContextProvider } from '@wix/da-shared-react/pkg/biLogger';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import ProfileHeroV2 from '../UserWidgets/ProfileHeroV2';
import ProfileHeroFTUE from '../UserWidgets/ProfileHeroV2/ProfileHeroFTUE';
import DraggableProfileHero from '../UserWidgets/ProfileHeroV2/DraggableProfileHero';
import ProfileHeroEditControls from '../UserWidgets/ProfileHeroV2/ProfileHeroEditControls';
import ViewAsVisitorBar from '../ViewAsVisitorBar';
import { routes } from '../../routes';

import s from './App.scss';

export const CONTENT_CONTAINER_ID = 'content-container';

export interface Props {
  activeSection: string;
  language: string;
  isMobile: boolean;
  isOwner?: boolean;
  profileOwnerId: number;
  isSectionLoading?: boolean;
  isViewingAsVisitor?: boolean;
  theme?: ThemeOptions;
  userAgent?: string;
  environment?: RuntimeEnvironment;
  showProfileHeroAid?: boolean;
  showProfileEditAid?: boolean;
  isHeroEditingActive?: boolean;
  appUrl: string;
  onAppRendered(): void;
  onPathChange(location: object): void;
  cdc_posts_inline?: boolean;
}

export interface State {
  isIntroPopupOpen: boolean;
}

class App extends React.Component<Props & RouteComponentProps<any>, State> {
  static defaultProps = {
    language: 'en',
  };

  state = {
    isIntroPopupOpen: false,
  };

  pageContentContainer: React.RefObject<any> = React.createRef();
  sectionContentContainer: React.RefObject<any> = React.createRef();

  private doneShowingEditControls = false;

  componentDidMount() {
    this.props.onAppRendered();
    const pageContainerClassList = this.pageContentContainer.current!.classList;
    pageContainerClassList.add(s['show-edit-controls']);
    // on SSR rendering we show edit controls
    // and hide them after 2 seconds here using direct node manipulation
    // to be make it more performant
    // we also need to set this.doneShowingEditControls so that
    // a subsequent re-render doesn't make them re-appear
    setTimeout(() => {
      pageContainerClassList.remove(s['show-edit-controls']);
      this.doneShowingEditControls = true;
      // this is not done via state to not re-render App.tsx in a bit after page load
      // while the user is likely to be scrolling
    }, 2000);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (
      nextProps.theme !== this.props.theme ||
      nextProps.showProfileHeroAid !== this.props.showProfileHeroAid ||
      nextProps.showProfileEditAid !== this.props.showProfileEditAid ||
      nextProps.isHeroEditingActive !== this.props.isHeroEditingActive ||
      nextState.isIntroPopupOpen !== this.state.isIntroPopupOpen ||
      nextProps.isViewingAsVisitor !== this.props.isViewingAsVisitor
    ) {
      return true;
    }

    const section = this.props.location.pathname.split('/')[2];
    if (['gallery', 'favourites'].includes(section)) {
      const nextSection = nextProps.location.pathname.split('/')[2];
      if (section !== nextSection) {
        this.props.onPathChange(nextProps.location);
      }
    } else {
      // Using not full url to make navigation to <a name /> by hash works
      // or passing params to section data loader
      if (nextProps.location.pathname !== this.props.location.pathname) {
        this.props.onPathChange(nextProps.location);
      }
    }

    return this.props.isSectionLoading !== nextProps.isSectionLoading;
  }

  render() {
    const {
      language,
      environment,
      activeSection,
      profileOwnerId,
      showProfileHeroAid,
      isHeroEditingActive,
      isViewingAsVisitor,
      appUrl,
      cdc_posts_inline,
    } = this.props;

    return (
      <DaApp
        language={language}
        environment={environment}
        bodyClassName={classnames(isHeroEditingActive && s['disable-scroll'])}
        backToTopOffset={0}
        appUrl={appUrl}
      >
        <SectionProvider value={activeSection}>
          <BiLoggerContextProvider
            value={{
              typeid: ResourceType.USER,
              itemid: profileOwnerId,
            }}
          >
            <SiteHeader environment={environment} />
            {isViewingAsVisitor && <ViewAsVisitorBar />}
            {isHeroEditingActive ? (
              <DraggableProfileHero className={s['profile-hero']} />
            ) : (
              <ProfileHeroV2
                showProfileHeroAid={showProfileHeroAid}
                className={s['profile-hero']}
              />
            )}
            {this.state.isIntroPopupOpen && (
              <ProfileHeroIntroPopup onClose={this.handleCloseIntroPopup} />
            )}
            <div
              className={classnames(
                s['above-hero-content'],
                isHeroEditingActive && s['editing-hero'],
                cdc_posts_inline && s['cdc_posts_inline']
              )}
            >
              {this.renderContent()}
              <SiteFooter className={s['special-profile-footer']} />
            </div>
          </BiLoggerContextProvider>
        </SectionProvider>
      </DaApp>
    );
  }

  renderContent() {
    const { isMobile } = this.props;
    const showEditControls =
      !this.doneShowingEditControls && this.props.isOwner;
    const { showProfileHeroAid, showProfileEditAid } = this.props;
    return (
      <div
        id={CONTENT_CONTAINER_ID}
        ref={this.pageContentContainer}
        className={classnames(
          s['content-container'],
          showEditControls && s['show-edit-controls'],
          showProfileHeroAid && s['show-ftue-controls']
        )}
      >
        <div className={s['profile-hero-overlay']} />
        <div className={s['content-container-with-paddings']}>
          {showProfileHeroAid && (
            <ProfileHeroFTUE
              className={s['profile-hero-ftue']}
              openIntroPopup={this.handleOpenIntroPopup}
              showProfileEditAid={showProfileEditAid}
            />
          )}
          <ErrorBoundary>
            <div className={s['profile-identity-container']}>
              <ProfileIdentity className={s['profile-identity']} />
              {isMobile && (
                <MoreActionsDropdownMobile className={s['more-actions']} />
              )}
            </div>
          </ErrorBoundary>
          <SubNavBar />
          <div
            ref={this.sectionContentContainer}
            className={s['section-container']}
          >
            {renderRoutes(routes)}
          </div>
        </div>
        {this.renderProfileHeroEdit()}
      </div>
    );
  }

  renderProfileHeroEdit = () => {
    const {
      isOwner,
      isMobile,
      showProfileHeroAid,
      showProfileEditAid,
    } = this.props;

    if (!isOwner || isMobile) {
      return null;
    }
    return (
      <ProfileHeroEditControls
        className={classnames(
          s['profile-hero-edit'],
          showProfileHeroAid && s['show-ftue-controls']
        )}
        openIntroPopup={this.handleOpenIntroPopup}
        showProfileHeroAid={showProfileHeroAid}
        showProfileEditAid={showProfileEditAid}
      />
    );
  };

  handleOpenIntroPopup = () => {
    this.setState({ isIntroPopupOpen: true });
  };

  handleCloseIntroPopup = () => {
    this.setState({ isIntroPopupOpen: false });
  };
}

export default App;
